import { CURRENT_APP_QUERY_PARAMETER } from "@/constants/current_app";
import { gwStorefrontAppInfoToken, modalManagerToken } from "@/tokens";
import {
    LeaveReviewPageApplicationConfigToken,
    reviewsApplicationLoggerToken,
    reviewsAuthApiClientToken,
    reviewsFormModalToken,
    reviewsIconCollectionManagerToken,
} from "@apps/reviews/tokens";
import ApiClient from "@lib/ApiClient";
import ApiClientWithMetrics from "@lib/ApiClient/ApiClientWithMetrics";
import AuthApiClient from "@lib/ApiClient/AuthApiClient";
import MinilogLogger from "@lib/Logger";
import { AnalyticsEventsCollectorModule } from "@modules/analytics_events_collector/AnalyticsEventsCollectorModule";
import { CommonModule } from "@modules/common_module/CommonModule";
import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
import { TokenManagerModule } from "@modules/token_manager/TokenManagerModule";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import { filter, take, tap } from "rxjs";
import { assert } from "superstruct";
import { container, inject, instanceCachingFactory, singleton } from "tsyringe";

import { ApplicationInterface } from "@interfaces/ApplicationInterface";

import {
    LeaveReviewPageWidgetDefinition,
    ReviewsFormModalWidgetDefinition,
} from "./definitions";
import {
    LeaveReviewPageApplicationConfigInterface,
    LeaveReviewPageApplicationConfigSchema,
} from "./LeaveReviewPageApplicationConfig";
import ReviewsStorefrontValuesService from "./services/ReviewsStorefrontValuesService";
import ReviewsIconCollectionManager from "./utils/ReviewsIconCollectionManager";
import LeaveReviewPageWidgetInjector from "./widgets/leave_review_page_widget/LeaveReviewPageWidgetInjector";
import ReviewsFormModalWidgetInjector from "./widgets/review_form_modal_widget/ReviewsFormModalWidgetInjector";

@singleton()
export class LeaveReviewPageApplication
    implements ApplicationInterface<object>
{
    widgets = [];
    exports = {};

    constructor(
        @inject(LeaveReviewPageApplicationConfigToken)
        public readonly config: LeaveReviewPageApplicationConfigInterface,
        @inject(AnalyticsEventsCollectorModule)
        private readonly analyticsEventsCollectorModule: AnalyticsEventsCollectorModule,
        @inject(CommonModule)
        private readonly commonModule: CommonModule,
        @inject(DynamicImportModule)
        private readonly dynamicImportModule: DynamicImportModule,
        @inject(MutationObserverInjector)
        private readonly mutationObserverInjector: MutationObserverInjector
    ) {
        assert(config, LeaveReviewPageApplicationConfigSchema);

        const { dynamicImportManager } = this.dynamicImportModule.exports;

        dynamicImportManager.register(ReviewsFormModalWidgetDefinition);
        dynamicImportManager.register(LeaveReviewPageWidgetDefinition);
    }

    init(): this {
        const logger = new MinilogLogger("reviews");
        const reviewsStorefrontValuesService = container.resolve(
            ReviewsStorefrontValuesService
        );
        const reviewsBranding$ =
            reviewsStorefrontValuesService.selectReviewsBrandingOptions();

        container.registerInstance(reviewsApplicationLoggerToken, logger);

        container.register(reviewsAuthApiClientToken, {
            useFactory: instanceCachingFactory((di) => {
                return new AuthApiClient(
                    new ApiClientWithMetrics(
                        new ApiClient(
                            this.commonModule.exports.baseUrlProvider.appendToBaseUrl(
                                "/storefront-api/reviews-storefront/v2"
                            ),
                            undefined,
                            {
                                [CURRENT_APP_QUERY_PARAMETER]: di.resolve(
                                    gwStorefrontAppInfoToken
                                ).currentApp,
                                ...this.commonModule.exports
                                    .designModeQueryParamsProvider.queryParams,
                            },
                            di.resolve(reviewsApplicationLoggerToken)
                        ),
                        this.analyticsEventsCollectorModule.exports.eventsCollector,
                        this.commonModule.exports.locationController,
                        this.analyticsEventsCollectorModule.exports.eventsFactory
                    ),
                    di.resolve(TokenManagerModule).exports.tokenManager
                );
            }),
        });

        container.register(reviewsIconCollectionManagerToken, {
            useFactory: instanceCachingFactory((di) => {
                return new ReviewsIconCollectionManager(
                    reviewsBranding$,
                    di.resolve(DynamicImportModule).exports.dynamicImportManager
                );
            }),
        });

        logger.debug("LeaveReviewPageApplication is initialized", {
            ...this.config,
        });

        // Reviews form modal manager
        const modalManager = container.resolve(modalManagerToken);
        modalManager.addModal(reviewsFormModalToken);

        const reviewsFormModalWidgetInjector =
            new ReviewsFormModalWidgetInjector(
                this.config.reviewsFormModalWidgetPlaceholderSelector,
                logger,
                this.analyticsEventsCollectorModule.exports.eventsCollector,
                this.analyticsEventsCollectorModule.exports.eventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager,
                this.mutationObserverInjector
            );

        const leaveReviewPageWidgetInjector = new LeaveReviewPageWidgetInjector(
            this.config.leaveReviewPageWidgetPlaceholderSelector,
            logger,
            this.analyticsEventsCollectorModule.exports.eventsCollector,
            this.analyticsEventsCollectorModule.exports.eventsFactory,
            this.dynamicImportModule.exports.dynamicImportManager,
            this.mutationObserverInjector
        );

        reviewsFormModalWidgetInjector.start();
        leaveReviewPageWidgetInjector.start();

        // Dynamic import ReviewsFormModalWidget
        modalManager
            .selectIsOpen(reviewsFormModalToken)
            .pipe(
                filter((showModal) => showModal),
                tap(() => {
                    void this.dynamicImportModule.exports.dynamicImportManager.fetch(
                        {
                            definition: ReviewsFormModalWidgetDefinition,
                        }
                    );
                }),
                take(1)
            )
            .subscribe();

        return this;
    }
}
